import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Archive, 
  Calendar, 
  Cloud, 
  TrendingUp,
  Luggage,
  Users,
  Heart,
  Share2
} from 'lucide-react';
import { ClosetItem } from '@/hooks/useCloset';
import { cn } from '@/lib/utils';

interface ObservationDashboardProps {
  display?: 'outfit' | 'wardrobe' | 'calendar' | 'trip' | 'chat' | 'camera' | 'feed' | 'packing';
  data?: any;
  weather?: any;
  wardrobeCount?: number;
  onItemClick?: (item: ClosetItem) => void;
  onAction?: (action: any) => void;
}

export const ObservationDashboard: React.FC<ObservationDashboardProps> = ({
  display = 'chat',
  data,
  weather,
  wardrobeCount = 0,
  onItemClick,
  onAction,
}) => {
  return (
    <AnimatePresence mode="wait">
      {/* Quick stats when in chat mode */}
      {display === 'chat' && (
        <motion.div
          key="chat"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="p-4"
        >
          <div className="grid grid-cols-3 gap-2">
            <QuickStat
              icon={<Archive size={14} />}
              label="Archive"
              value={wardrobeCount.toString()}
            />
            <QuickStat
              icon={<Cloud size={14} />}
              label={weather?.location?.split(',')[0] || 'Weather'}
              value={weather ? `${weather.temp}°` : '--'}
              highlight={weather?.condition?.includes('Rain')}
            />
            <QuickStat
              icon={<TrendingUp size={14} />}
              label="Ready"
              value="∞"
            />
          </div>
        </motion.div>
      )}

      {/* Outfit display */}
      {display === 'outfit' && data && (
        <motion.div
          key="outfit"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="p-4"
        >
          <div className="bg-card border border-border rounded-2xl p-4 overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-aura-gold" />
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  {data.occasion || "Today's Look"}
                </span>
              </div>
              {data.score && (
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium text-aura-gold">{data.score}</span>
                  <span className="text-[10px] text-muted-foreground">/100</span>
                </div>
              )}
            </div>

            {/* Outfit items grid */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[data.top, data.bottom, data.shoes].filter(Boolean).map((item: ClosetItem, idx: number) => (
                <motion.div
                  key={item?.id || idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  className="aspect-square rounded-xl overflow-hidden bg-muted relative group"
                >
                  {item?.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Archive size={20} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="absolute bottom-1 left-1 right-1 text-[9px] text-white truncate">
                      {item?.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Accessory if present */}
            {data.accessory && (
              <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-secondary/30">
                <div className="w-8 h-8 rounded-lg overflow-hidden bg-muted">
                  {data.accessory.image_url ? (
                    <img src={data.accessory.image_url} alt={data.accessory.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Sparkles size={12} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">+ {data.accessory.name}</span>
              </div>
            )}

            {/* Style tip */}
            {data.styleTip && (
              <p className="text-xs text-muted-foreground italic border-l-2 border-aura-gold/30 pl-3">
                "{data.styleTip}"
              </p>
            )}
          </div>
        </motion.div>
      )}

      {/* Wardrobe grid display */}
      {display === 'wardrobe' && data?.items && (
        <motion.div
          key="wardrobe"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Your Archive
            </span>
            <span className="text-xs text-muted-foreground">
              {data.items.length} items
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {data.items.slice(0, 8).map((item: ClosetItem, idx: number) => (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.03 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onItemClick?.(item)}
                className="aspect-square rounded-xl overflow-hidden bg-muted relative group"
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <Archive size={14} />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1.5">
                  <span className="text-white text-[9px] truncate w-full">{item.name}</span>
                </div>
              </motion.button>
            ))}
          </div>
          {data.items.length > 8 && (
            <p className="text-center text-xs text-muted-foreground mt-2">
              + {data.items.length - 8} more items
            </p>
          )}
        </motion.div>
      )}

      {/* Calendar display */}
      {display === 'calendar' && (
        <motion.div
          key="calendar"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="p-4"
        >
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={14} className="text-aura-gold" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Upcoming Looks</span>
            </div>
            {data?.events?.length > 0 ? (
              <div className="space-y-2">
                {data.events.slice(0, 3).map((event: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30">
                    <div className="text-center">
                      <p className="text-xs font-medium">{new Date(event.date).getDate()}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(event.date).toLocaleDateString('en', { weekday: 'short' })}
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm">{event.occasion || 'Scheduled Look'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No outfits scheduled. Ask me to plan your week.
              </p>
            )}
          </div>
        </motion.div>
      )}

      {/* Trip/Packing display */}
      {(display === 'trip' || display === 'packing') && (
        <motion.div
          key="packing"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="p-4"
        >
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Luggage size={14} className="text-aura-gold" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {data?.tripName || 'Packing List'}
              </span>
            </div>
            {data?.items?.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {data.items.slice(0, 8).map((item: any, idx: number) => (
                  <div 
                    key={idx} 
                    className={cn(
                      "aspect-square rounded-xl overflow-hidden bg-muted relative",
                      item.packed && "ring-2 ring-green-500"
                    )}
                  >
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-[10px] p-1 text-center">
                        {item.name}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Tell me where you're going and I'll help you pack.
              </p>
            )}
          </div>
        </motion.div>
      )}

      {/* Feed display */}
      {display === 'feed' && (
        <motion.div
          key="feed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="p-4"
        >
          <div className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users size={14} className="text-aura-gold" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Syndicate</span>
            </div>
            {data?.posts?.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {data.posts.slice(0, 6).map((post: any, idx: number) => (
                  <div key={idx} className="aspect-square rounded-xl overflow-hidden bg-muted relative">
                    {post.image_url && (
                      <img src={post.image_url} alt="" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute bottom-1 left-1 flex items-center gap-1 text-white text-[10px]">
                      <Heart size={10} />
                      {post.likes || 0}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                The Syndicate awaits your looks. Share your first outfit!
              </p>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Quick stat component
const QuickStat: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}> = ({ icon, label, value, highlight }) => (
  <motion.div 
    whileHover={{ scale: 1.02 }}
    className={cn(
      "bg-card border rounded-xl p-3 text-center transition-colors",
      highlight ? "border-aura-gold/50 bg-aura-gold/5" : "border-border"
    )}
  >
    <div className="flex items-center justify-center text-muted-foreground mb-1">
      {icon}
    </div>
    <p className="text-base font-semibold text-foreground">{value}</p>
    <p className="text-[9px] text-muted-foreground uppercase tracking-wider truncate">{label}</p>
  </motion.div>
);
